import { useCallback, useState, useMemo, useEffect } from 'react';

import { useUrlState, useDebounce, compareFilters, isDefaultFilters } from '@/shared/lib';
import { SERVICE_REQUEST_STATUS } from '@/entities/service-request';

import type { ServiceRequestUrlState } from '../model/types';
import { DEFAULT_URL_STATE } from './constants';

export const useServiceRequestsFilters = () => {
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [localFilters, setLocalFilters] = useState<ServiceRequestUrlState>(() => urlState);
  const [searchQuery, setSearchQuery] = useState(() => urlState.search);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    if (urlState.status && urlState.status !== SERVICE_REQUEST_STATUS.ALL) {
      count++;
    }

    return count;
  }, [urlState]);

  const hasActiveFilters = activeFiltersCount > 0;

  const isFiltersDirty = useMemo(() => {
    return !compareFilters(localFilters, urlState, ['search']);
  }, [localFilters, urlState]);

  const hasDefaultFilters = useMemo(() => {
    return isDefaultFilters(urlState, DEFAULT_URL_STATE, ['search']);
  }, [urlState]);

  const handleFilterChange = useCallback((key: keyof ServiceRequestUrlState, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleReset = useCallback(() => {
    setLocalFilters(DEFAULT_URL_STATE);
    setSearchQuery('');
    resetUrlState();
  }, [resetUrlState]);

  const handleApplyFilters = useCallback(() => {
    const { search, ...filtersWithoutSearch } = localFilters;
    setUrlState(filtersWithoutSearch);
  }, [localFilters, setUrlState]);

  useEffect(() => {
    setLocalFilters(urlState);
  }, [urlState]);

  useEffect(() => {
    if (debouncedSearchQuery !== urlState.search) {
      setUrlState({ search: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, setUrlState, urlState.search]);

  return {
    // State
    urlState: localFilters,
    searchQuery,
    activeFiltersCount,
    hasActiveFilters,
    isFiltersDirty,
    hasDefaultFilters,

    // Handlers
    handleFilterChange,
    handleSearchChange,
    handleReset,
    handleApplyFilters
  };
};
